﻿import { useState, useEffect, type ChangeEvent } from "react";
import { Box, TextField, Autocomplete } from "@mui/material";
import {
  ShepCheckbox,
  ShepSearchBar,
  ShepTab,
  ShepTabs,
  ShepHorizontalMenu,
  ShepGridWarning,
} from "@/components/GenericComponents/ShepComponents";
import { createAlert } from "@/features/homepage/alertSliceList";
import { deleteCatalogData, type IChildCatalogIdAndCode } from "@/features/connections/manageCatalogDataSlice";
import { useGridApiContext } from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/typedDispatch";
import {
  fetchConnectionNames,
  selectConnectionNamesRecords,
  selectSelectedConnection,
  setSelectedConnection,
  type IConnectionRecord,
} from "@/features/connections/integrationSlice";
import {
  fetchCatalogFields,
  fetchCatalogSummaries,
  fetchItemQualifiers,
  selectCatalogSummarySearchText,
  selectCatalogSummarySelectedTab,
  setCatalogSummarySearchText,
  setCatalogSummarySelectedTab,
} from "@/features/maintenance/maintenanceSlice";
import DownIcon from "@/assets/img/icons/Down/Line.svg?react";
import "./Maintenance.css";
import {
  importTabTypesEnum,
  setFileImportPopupOpen,
} from "@/features/maintenance/fileImportSlice";
import axios from "@/features/axiosHelper";
import { CatalogTypes } from "./MaintenanceTypes";
import CustomCatalogFieldPopup from "./CatalogCustomFieldPopup";
import {
  setCatalogType,
  setIsAddCatalogOpen,
} from "../../features/connections/manageCatalogDataSlice";
import type { UUIDTypes } from "uuid";

let searchCancellation: number;

const CatalogGridToolbar = () => {
  const allConnections = useSelector(selectConnectionNamesRecords);
  const currentConnection = useSelector(selectSelectedConnection);
  const currentCatalog = useSelector(selectCatalogSummarySelectedTab);
  const searchText = useSelector(selectCatalogSummarySearchText);
  const apiRef = useGridApiContext();
  const dispatch = useAppDispatch();
  const [customFieldOpen, setCustomFieldOpen] = useState(false);

  const handleDataTypeTabChange = (_e: ChangeEvent<HTMLInputElement>, newValue: number) => {
    dispatch(setCatalogSummarySelectedTab({ selectedTab: newValue }));
    apiRef.current.setPage(0);
    fetchCatalogs(searchText);
    // heap.io

    const tabDict: {
      [key: number]: string;
    } = {};

    tabDict[CatalogTypes.Customer] = "Customer";
    tabDict[CatalogTypes.Item] = "Item";
    tabDict[CatalogTypes.ItemUOM] = "Item UOM";
    tabDict[CatalogTypes.Warehouse] = "Warehouse";
    tabDict[CatalogTypes.ShipTo] = "Ship To";
    tabDict[CatalogTypes.Carrier] = "Carrier";
    tabDict[CatalogTypes.AllowanceOrCharge] = "Allowance/Charge";

    const tabLabel = tabDict[newValue];

    if (tabLabel !== undefined) {
      const eventProps = {
        "Catalog Data Type": tabLabel,
      };

      window.heap.track("Catalog Data - Click Subtab", eventProps);
    }
  };

  const fetchCatalogs = (searchText: string) => {
    dispatch(
      fetchCatalogSummaries({
        pageSize: apiRef.current.state.pagination.paginationModel.pageSize,
        unloadRecords: true,
        search: searchText,
      }),
    );
  };

  const handleConnectionChange = (
    newValue: IConnectionRecord | null
  ) => {
    if (!newValue) {
      return;
    }
    const {
      Id: value,
      Name: connectionName
    } = newValue;
    dispatch(
      setSelectedConnection({
        selectedConnectionInfo: {
          ConnectionId: value,
          ConnectionName: connectionName
        },
      }),
    );
    apiRef.current.setPage(0);
    fetchCatalogs(searchText);

    // heap.io
    window.heap.track("Catalog Data - Select Connection");
  };

  useEffect(() => {
    return () => {
      dispatch(setSelectedConnection({ selectedConnectionInfo: null }));
    };
  }, [dispatch]);

  useEffect(() => {
    if (currentCatalog === CatalogTypes.Item) {
      dispatch(fetchItemQualifiers({ connectionId: currentConnection?.ConnectionId ?? -1 }));
    }
    dispatch(fetchCatalogFields({ connectionId: currentConnection?.ConnectionId ?? -1 }))  
  }, [currentConnection, currentCatalog, dispatch]);

  const handleExecuteSearch = (searchText: string) => {
    apiRef.current.setPage(0);
    fetchCatalogs(searchText);

    // heap.io
    window.heap.track("Catalog Data - Use Search Bar");
  };

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setCatalogSummarySearchText(event.target.value));
    clearTimeout(searchCancellation);
    searchCancellation = setTimeout(
      () => handleExecuteSearch(event.target.value),
      500,
    );
  };

  interface IPostCatalogChildrenResponse {
    [key: string]: IChildCatalogIdAndCode[];
  }
  const handleDelete = async () => {
    const rowIds: UUIDTypes[] = [];
    const rows = apiRef.current.getSelectedRows();
    rows.forEach((rowData) => {
      rowIds.push(rowData["CatalogId"]);
    });
    if (rowIds.length) {
      const catalogIds = rowIds;
      const response = await axios.post<IPostCatalogChildrenResponse>(
        "/connectioncatalogartifacts/catalog-children",
        catalogIds,
        {
          params: {
            catalogType: currentCatalog,
          },
        },
      );
      const catalogsWithChildren = response.data;

      const numChildren = Object.keys(catalogsWithChildren).reduce(
        (accumulator, currentValue) => {
          return accumulator + catalogsWithChildren[currentValue].length;
        },
        0,
      );

      const titleAddition = `(and ${numChildren} associated)`;

      const isSingularRow = rows.size === 1;
      const title =
        (isSingularRow
          ? "This Catalog "
          : `The ${rows.size} Selected Catalogs `) +
        titleAddition +
        " will be deleted";

      const deletingSingle = numChildren === 0 && isSingularRow;
      const message = `Are you sure you want to delete ${
        deletingSingle ? "this record?" : "these records?"
      }`;
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "result",
          title: title,
          message: message,
          buttonLabel: "Yes",
          buttonLabel2: "No",
          onButtonClick: () =>
            deleteCatalog(catalogsWithChildren),
        }),
      );
    } else {
      dispatch(
        createAlert({
          status: "Error",
          message: "Please select the records you want to delete first.",
        }),
      );
    }
  };

  const deleteCatalog = (catalogs: IPostCatalogChildrenResponse) => {
    dispatch(deleteCatalogData({ selectedMappings: catalogs }))
      .unwrap()
      .then((deleteStatus) => {
        dispatch(
          fetchCatalogSummaries({
            page: apiRef.current.state.pagination.paginationModel.page,
            pageSize: apiRef.current.state.pagination.paginationModel.pageSize,
            unloadRecords: true,
          }),
        );
        if (deleteStatus.Deleted === 0) {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              title: "Deletion Failed!",
              message: deleteStatus.Message,
              buttonLabel: "Close",
            }),
          );
        } else {
          dispatch(
            createAlert({
              status: "Success",
              alertType: "result",
              title: "Deletion Successful!",
              message: deleteStatus.Message,
              buttonLabel: "Close",
            }),
          );
        }

        // heap.io
        window.heap.track("Catalog Data - Delete");
      })
      .catch(() => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "result",
            title: "Deletion failed.",
            message:
              "Error deleting catalog data, Please check if the record is referenced in Cross-reference.",
            buttonLabel: "Close",
          }),
        );
      });
  };

  useEffect(() => {
    dispatch(fetchConnectionNames());
  }, [dispatch]);

  const handleExport = async () => {
    if (!validateSelected()) {
      dispatch(
        createAlert({
          status: "Error",
          message: "Please select a connection before attempting to export.",
        }),
      );
      return;
    }
    const selectedRows = apiRef.current.getSelectedRows();
    const selectedRowIds = Array.from(selectedRows.values()).map(
      (row) => row.CatalogId,
    );

    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const catalogType = Object.keys(CatalogTypes).filter(
      (x) => CatalogTypes[x as keyof typeof CatalogTypes] === currentCatalog,
    );
    const filename = `catalog_data_${catalogType}_${timestamp}.csv`;

    try {
      const response = await axios.get(
        "/connectioncatalogartifacts/export-catalog-data",
        {
          params: {
            catalogType: currentCatalog,
            selectedCatalogIdsString: selectedRowIds.join(","),
            connectionId: currentConnection?.ConnectionId ?? -1,
            searchText: searchText.trim(),
          },
        },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // heap.io
      window.heap.track("Catalog Data - Export");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      dispatch(
        createAlert({
          status: "Error",
          message: "Error exporting data: " + error.response.data,
        }),
      );
      console.error("Error exporting data:", error.response.data);
    }
  };

  const handleAddCatalogOpen = () => {
    if (!validateSelected()) {
      dispatch(
        createAlert({
          status: "Error",
          message:
            "Please select a connection before attempting to add catalog.",
        }),
      );
      return;
    }
    const currentCon = allConnections.filter(
      (x) => x.ConnectionId == currentConnection?.ConnectionId,
    )[0];
    dispatch(
      setIsAddCatalogOpen({
        isOpen: true,
        isEdit: false,
        connectionInfo: {
          displayName: currentCon.DisplayName,
          connectionId: currentCon.ConnectionId,
        },
        catalogType: currentCatalog,
      }),
    );
    dispatch(setCatalogType({ catalogType: currentCatalog }));
  };

  const handleAddCustomFieldOpen = () => {
    setCustomFieldOpen(true);
  };

  const validateSelected = () => {
    if (
      !currentConnection ||
      currentConnection.ConnectionId === -1
    ) {
      return false;
    }
    return true;
  };

  const actions = [
    {
      label: "Import",
      onClick: () =>
        dispatch(
          setFileImportPopupOpen({
            open: true,
            tabType: importTabTypesEnum.catalog,
            dataType: currentCatalog,
          }),
        ),
    },
    { label: "Export", onClick: handleExport },
    { label: "Add", onClick: handleAddCatalogOpen },
    { label: "Custom Fields", onClick: handleAddCustomFieldOpen },
    { label: "Delete", onClick: handleDelete, variant: "danger" },
  ];

  return (
    <div>
      <ShepTabs
        variant="gridSelector"
        value={currentCatalog}
        onChange={handleDataTypeTabChange}
        sx={{ display: "flex", flexDirection: "row", width: "100%" }}
      >
        <ShepTab
          variant="gridSelector"
          label={"Customer"}
          value={CatalogTypes.Customer}
          aria-controls="tabpanel-0"
        />
        <ShepTab
          variant="gridSelector"
          label={"Item"}
          value={CatalogTypes.Item}
          aria-controls="tabpanel-1"
        />
        <ShepTab
          variant="gridSelector"
          label={"Item UOM"}
          value={CatalogTypes.ItemUOM}
          aria-controls="tabpanel-2"
        />
        <ShepTab
          variant="gridSelector"
          label={"Warehouse"}
          value={CatalogTypes.Warehouse}
          aria-controls="tabpanel-3"
        />
        <ShepTab
          variant="gridSelector"
          label={"Ship To"}
          value={CatalogTypes.ShipTo}
          aria-controls="tabpanel-4"
        />
        <ShepTab
          variant="gridSelector"
          label={"Carrier"}
          value={CatalogTypes.Carrier}
          aria-controls="tabpanel-5"
        />
        <ShepTab
          variant="gridSelector"
          label={"Allowance/Charge"}
          value={CatalogTypes.AllowanceOrCharge}
          aria-controls="tabpanel-6"
        />
        <ShepTab
          variant="gridSelector"
          label={"Tax Group"}
          value={CatalogTypes.TaxGroup}
          aria-controls="tabpanel-7"
        />
      </ShepTabs>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "25px 0",
          backgroundColor: "var(--disabled)",
        }}
      >
        <Autocomplete
          sx={{
            ".MuiInputBase-input": { height: "0px" },
          }}
          popupIcon={<DownIcon style={{ width: 16, height: 16 }} />}
          options={allConnections}
          getOptionLabel={(option) => option.DisplayName}
          isOptionEqualToValue={
            (option, value) => option.ConnectionId === value.ConnectionId //doubt here - Arathy
          }
          onChange={(_e, newValue) => handleConnectionChange(newValue)}
          renderOption={(props, option, { selected }) => {
            // shouldn't be passing optional props in bulk
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { key, ...optionProps } = props;
            return (
              <li key={option.Id} value={option.Id} {...optionProps}>
                {/* @ts-expect-error --checked is not defined as a property on ShepCheckbox */}
                <ShepCheckbox checked={selected} />
                {option.DisplayName}
              </li>
            );
          }}
          style={{ width: 600 }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select Connection" />
          )}
        />
        <ShepSearchBar
          placeholder="Search Reference Type"
          value={searchText}
          onChange={handleSearch}
          sx={{ width: 600, marginLeft: "20px" }}
        />
        <Box sx={{ marginLeft: "auto", display: "flex" }}>
          <ShepHorizontalMenu actions={actions} />
        </Box>
      </div>
      {!currentConnection || Object.keys(currentConnection).length === 0 && (
        <ShepGridWarning
          title="No Connection Selected."
          message="Select a connection to display the data table."
          sx={{ minHeight: "100px" }}
        />
      )}

      <CustomCatalogFieldPopup
        open={customFieldOpen}
        onClose={() => setCustomFieldOpen(false)}
      />
    </div>
  );
};

export default CatalogGridToolbar;
